/**
 * This is the legacy LaunchDarkly client that we need to remove by Dec, 2024 but is in place till
 * https://hello.jira.atlassian.cloud/browse/KIRBY-6029 is completed.
 *
 * The new client is found in FeatureGateProvider. This is a wrapper around StatSig.
 * https://hello.atlassian.net/wiki/spaces/MEASURE/pages/2662710507/How+to+Integrate+with+Statsig
 */
import React, { ReactNode, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';

import { AnalyticsClientImpl } from '../utilities/analytics/analytics-web-client';
import { captureException } from '../utilities/analytics/error-reporting';
import { getFfsId, initializeFeatureFlagClient } from '../utilities/feature-flags';

type FeatureFlagProviderProps = {
  children: ReactNode;
  analyticsClient: AnalyticsClientImpl;
};

export const FeatureFlagProvider = ({ children, analyticsClient }: FeatureFlagProviderProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const ffsId = getFfsId();
        const client = initializeFeatureFlagClient(analyticsClient);
        await client.ready();

        const flags = client.getFlags();

        Sentry.withScope(scope => {
          Object.keys(flags).forEach(key => scope.setExtra(key, flags[key]));
        });

        if (ffsId) {
          analyticsClient.setFfsId(ffsId);
        }
      } catch (e) {
        console.warn('[FeatureFlagClient] Using default feature flags');
        captureException(e, { message: 'Failed to load @atlassiansox/feature-flag-web-client' });
      } finally {
        setIsLoaded(true);
      }
    };

    if (!isLoaded) {
      load();
    }
  }, [isLoaded, analyticsClient]);

  if (!isLoaded) {
    return null;
  }

  return <>{children}</>;
};
